import React from "react";
import {UserButtons} from "@/main/react/user/UserButtons";
import DateFormatter from "@/main/common/DateFormatter";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import {Localizer} from "@/main/react/Types";
import {CalendarDays, List, MessageSquareQuote, MessageSquareText, ArrowBigUp, SlidersHorizontal, Sparkles, UserPlus2, Users} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMediaQueryMatches } from "@/main/react/hooks/useMediaQueryMatches";
import {ProfileBanner} from "@/main/react/component/ProfileBanner";
import SimpleSelect from "@/main/react/component/SimpleSelect";
import { ProfilePageSelectOption } from "@/main/react/page/profile-page/ProfilePageOrdering";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import { Badge } from "@/components/ui/badge";
import { BadgeCheck, HeartHandshake } from "lucide-react";
import { getDisplayUsername, normalizeDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import { useSupportUsername } from "@/main/navigation/useSupportUsername";
import { isSupportParticipant } from "@/main/react/header/sidebar/chat/chatShared";
import { onProfileUpdated } from "@/main/common/EventRegistry";
import { getFriendlyCountBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import { CardMetadataBadges } from "@/main/react/card/CardMetadataBadges";

type HeaderTabOption = {
    key: string;
    value: string;
    label: string;
    icon: React.ElementType;
};

/**
 * How much of the profile header this instance is responsible for, and at what measure.
 *
 * - `"wide"` — the historical shape: a full-bleed identity card followed by a right-aligned control
 *   row, both spanning one centred column. Still the only shape the component gallery renders, which
 *   is the reason it is kept alive rather than deleted once both real pages moved off it.
 * - `"rail"` — the card ALONE, sized for a 22rem column, with the control row extracted so the page
 *   can put it at the top of the main column where the reader is actually looking. Callers in this
 *   mode render {@link UserHeaderControls} themselves.
 *
 * Design: 2026-08-06-user-profile-rail-layout-and-achievements-showcase-design.md §2.6.
 */
export type UserHeaderLayout = "rail" | "wide";

/**
 * Which count chip was clicked, in the vocabulary of {@link buildStatBadges}' own `key`s.
 *
 * Deliberately NOT a section name: the public profile and the dashboard route to different section
 * vocabularies (`arguments` vs `arguments`+`authored`), and two of the six chips — the received-reaction
 * counts — have no list of their own and resolve onto the thoughts that earned them. Naming the CHIP and
 * letting each page own the mapping keeps that translation in one place per page instead of in here.
 * Destinations: 2026-08-11-header-count-chips-as-navigation-design.md §2.
 */
export type UserHeaderCountTarget =
    | "discussions"
    | "arguments"
    | "agreements"
    | "minds-changed"
    | "followers"
    | "following";

/**
 * The same six, as a value — so a host can ask "which of these resolves to the state I am already
 * in" without hand-keeping a second copy of the list (`EMPTY-STATES-85`). Typed as
 * `readonly UserHeaderCountTarget[]`, so adding a member to the union without adding it here is a
 * compile error rather than a chip that quietly never goes inert.
 */
export const USER_HEADER_COUNT_TARGETS: readonly UserHeaderCountTarget[] = [
    "discussions",
    "arguments",
    "agreements",
    "minds-changed",
    "followers",
    "following",
];

/**
 * The whole profile-header contract, exported because in `"rail"` mode it is served by TWO components
 * that must not drift: {@link UserHeader} renders the identity card into the rail, and
 * {@link UserHeaderControls} renders the section/sub-section/ordering row into the top of the main
 * column. Pages build ONE props object and spread it into both, so there is a single answer to "which
 * controls does this section show" rather than one per call site.
 */
export type UserHeaderProps = {
    username?: string;
    localizer: Localizer;
    initialProfileImageUrl?: string | null;
    initialDescription?: string | null;
    initialCreationDate?: string | null;
    section: string;
    onSectionChange: (v: string) => void;
    sectionOptions?: HeaderTabOption[];
    secondarySection?: string;
    onSecondarySectionChange?: (v: string) => void;
    secondaryOptions?: ProfilePageSelectOption[];
    orderingValue?: string;
    onOrderingChange?: (v: string) => void;
    orderingOptions?: ProfilePageSelectOption[];
    orderingLabel?: string;
    extraControls?: React.ReactNode;
    /**
     * How big the list the member is looking at actually is (`DASHBOARD-24`, `EMPTY-STATES-72`,
     * `EMPTY-STATES-100`; wave-12 sheet Q8 option A).
     *
     * <p>Rendered as quiet text immediately after the sub-section select, which is where Q8 placed it:
     * NOT as a chip, because every chip in {@link buildStatBadges} means "go there" and a size chip
     * would be the odd one out; and not as a visible region heading, because printing the select's own
     * words 40px below it is `EMPTY-STATES-34`.
     *
     * <p><b>A slot, not a `count` number.</b> Which of the six per-section sizes is the size of the
     * list on screen is a mapping from (section, sub-section) that only the page has — the dashboard
     * and the public profile route to different vocabularies, the same reason `onCountNavigate` takes a
     * chip name rather than a section. The header would have to keep a second copy of that mapping to
     * choose a number, and a second copy is how the number and the list come to disagree.
     *
     * <p>It renders inside the control row, so it inherits the row's `gap-2` and wraps with it. A slot
     * whose content is `null` produces no node at all, which is the honest rendering for a collection
     * this viewer may not see — the server sends `null`, never `0`, for those.
     */
    listCountSlot?: React.ReactNode;
    /**
     * The narrowing controls, which are allowed to fold away on a phone (`DASHBOARD-44`, wave-12 sheet
     * Q3 option A).
     *
     * <p>A SEPARATE slot from {@link extraControls} rather than a flag on it, because Q3 named exactly
     * two controls — ordering and language scope — and `extraControls` is an opaque node this component
     * cannot split. On the dashboard it also carries the deleted-content entry point, and that pill is
     * a ROUTE, not a filter: it exists precisely because the sub-tab select it duplicates was the
     * control members could not find (`DASHBOARD-6`). Folding it away would re-create the finding the
     * pill was added for. So: filters here, routes in `extraControls`.
     *
     * <p>Rendered inline, exactly where it renders today, unless {@link collapsibleFilters} is set AND
     * the pointer is coarse.
     */
    collapsibleControls?: React.ReactNode;
    /**
     * Fold the ordering select and {@link collapsibleControls} behind one "Filters" trigger on a coarse
     * pointer (`DASHBOARD-44` / `EMPTY-STATES-98`, wave-12 sheet Q3 option A).
     *
     * <p><b>Why it is opt-in.</b> The public profile shares this component and did not ask for the
     * collapse; the finding is about the dashboard, whose control row wraps to five ragged rows at
     * 412px (six in `es`) before the first card. Measured: 334 CSS px in `en` and 380.6 in `es`, i.e.
     * 36.6% and 41.6% of Pixel 7's 915 CSS px viewport, so a Spanish member opening their own dashboard
     * saw roughly one post.
     *
     * <p><b>Why a runtime branch and not a CSS pair.</b> Rendering an inline copy and a collapsed copy
     * and hiding one with a media query puts two `user-header-ordering-select` hooks in one document,
     * which is Playwright strict-mode failure — the same hazard {@link achievementsSlot}'s single-mount
     * contract exists for. `useMediaQueryMatches` is the repo's existing hook and there is exactly
     * one copy of each control at any width.
     *
     * <p><b>Why a disclosure and not a popover.</b> The folded controls are Radix selects; nesting them
     * inside a popover is avoidable risk for nothing. The trigger toggles a row that appears underneath,
     * which is all the finding needs: what it measures is the RESTING row.
     *
     * <p>Not URL state, and deliberately outside `useHistoryBackedState` (rule 33). The values inside
     * the panel already live in the address — `ordering` is a query parameter — and a disclosure is
     * ephemeral view state, not an address. Nothing here writes history.
     */
    collapsibleFilters?: boolean;
    /**
     * Whether a narrowing filter is currently applied, so the fold above starts OPEN rather than hiding
     * the control that says so.
     *
     * <p>Only the host can answer this: it owns the ordering vocabulary and whatever it put in
     * {@link collapsibleControls}. Read as the DEFAULT for the disclosure, never synced into it — the
     * member's own toggle wins once they have used it, and there is exactly one piece of state behind
     * the two (rule 31: never keep one value in two places and sync them both ways).
     */
    filtersActive?: boolean;
    /**
     * The trophy rail, rendered as the last row of the header card (achievements design §9.1).
     *
     * A slot rather than a `username` + fetch inside this component, for two reasons. This header also
     * serves the dashboard and the component gallery, and neither should acquire a network call by
     * being rendered; and the rail is code-split (rule 26), so it is the caller's job to decide whether
     * this page wants that chunk at all. The slot renders nothing when its content is `null`, which is
     * what the rail returns until the profile has its first award — so the card simply has no extra row
     * rather than an empty one.
     */
    achievementsSlot?: React.ReactNode;
    /**
     * The three profile-as-a-destination slots (2026-08-11), declared by the `agent/profile-seam`
     * structural pass so three feature agents could build concurrently.
     *
     * <p><b>Named and disjoint on purpose.</b> One generic `extraSlot` would put all three slices on the
     * same prop line and hand back the merge conflict the seam exists to remove; each agent's diff to
     * this 600-line shared component is exactly one line, and no two of them are the same line.
     *
     * <p>Same contract as {@link achievementsSlot} and for the same reason: rendered <b>bare, with no
     * wrapper</b>, so a slot whose content is `null` produces no DOM node and the surrounding `gap`
     * closes up on its own. A wrapper `<div>` here would hold an empty box open on every profile that
     * has none of these — decision §15.2's banned empty trophy case wearing a different hat.
     *
     * <p>⚠️ Every one of these renders INSIDE the existing `UserPage` island. None of them may introduce
     * a `react-component` container: `createRoot()` discards its element's children, so a nested island
     * is deleted the instant the parent chunk resolves. `user.jte` is the exact template of the
     * 2026-07-28 defect, and it presents as flakiness rather than absence.
     *
     * <p>Design: `designs/01-now/2026-08-11-profile-destination-slice-design.md` §8.
     */
    /** Slice F3 — the owner statement. Inside the identity card, under the description. */
    statementSlot?: React.ReactNode;
    /** Slice F1 — pinned items and surfaced stances. Below the card, in the rail column. */
    showcaseSlot?: React.ReactNode;
    /**
     * Newsletter growth D13 (round 44, slice 2c) — the member's public newsletters, one card each, below the
     * showcase. Same contract as the three above: bare, and `null` when there is none.
     */
    newsletterSlot?: React.ReactNode;
    /** Slice F2 — the owner-only visitor count. Below the showcase. */
    visitorInsightSlot?: React.ReactNode;
    /**
     * The page's own action controls (the dashboard's Metrics / My profile / Edit profile / Deleted
     * family), rendered inside the identity card where the default follow/message buttons otherwise
     * sit. From 2026-08-07 to 2026-08-27 a trio of `*InControlRow` props could hoist this slot, the
     * stat counts and the achievements strip into the control row instead — a reachability fix for a
     * card the dashboard painted after an infinite list on phones. The dashboard now paints its rail
     * first, like the public profile, and the props are deleted rather than left as zero-consumer
     * branches (rail layout design doc §10, 2026-08-27).
     */
    actionSlot?: React.ReactNode;
    showDefaultActions?: boolean;
    /**
     * What a click on the identity card's avatar means, decided by the HOST.
     *
     * <p>`SV-134`: on `UserPage` this is a TERMINAL site — the member is already on the profile the
     * avatar points at — so the host opens the full-size picture. On `MyDashboardPage` the same
     * component is a POINTER site and the host navigates to the profile instead. The header states
     * neither policy; it forwards the click and the URL it is currently rendering.
     *
     * <p>The URL is an argument rather than something the host reads from its own props because this
     * component re-points the picture in place when the member changes it (`onProfileUpdated`), so the
     * host's copy can be a version behind and would zoom the previous picture — a defect nothing would
     * report. Omitting it entirely leaves the avatar inert, exactly as before.
     */
    onAvatarClick?: (imageUrl?: string | null) => void;
    layout?: UserHeaderLayout;
    initiallyFollowing?: boolean;
    /**
     * The viewer-relative `canMessage` (safety plan Phase 1a, D16), handed to the default `UserButtons`.
     * Only the profile bootstrap supplies it; `undefined`/`null` keep the Message button as before.
     */
    canMessage?: boolean | null;
    /**
     * Welcome hosts D3 / A17 / A21: the "Welcome host" label, from `UserPageBootstrapDTO.welcomeHost`,
     * which the server computes (hosting, label not hidden, 5 answered welcomes). React-only: no
     * `user.jte` twin, exactly like the Official badge beside it. Absent reads as false.
     */
    welcomeHost?: boolean;
    sensitive?: boolean;
    discussionsCount?: number | null;
    argumentsCount?: number | null;
    agreementsReceivedCount?: number | null;
    /**
     * How many times this member's thoughts changed someone's mind. Null whenever the surface did not
     * ask for it — only the profile-bootstrap projection supplies it — and null and 0 render the same
     * way here, because `getFriendlyCountBadgeLabel` returns null at or below zero and the chip is
     * filtered out. That is deliberate: the count is genuinely zero for almost everyone, and a row of
     * "0 minds changed" chips would read as a scoreboard nobody asked to be on.
     */
    mindsChangedReceivedCount?: number | null;
    followersCount?: number | null;
    followingCount?: number | null;
    /**
     * Turns the count chips into the entry points to the sections they count.
     *
     * Optional, and the chips stay inert captions without it — `ComponentPage` renders this header in
     * the gallery with no page behind it, and a gallery chip that switched a section that does not exist
     * would be worse than one that does nothing.
     *
     * One callback rather than six handler props because {@link buildStatBadges} is already the single
     * list of which chips exist and in what order; a second parallel list of props is how the two would
     * come to disagree about it.
     */
    onCountNavigate?: (target: UserHeaderCountTarget) => void;
    /**
     * Chips whose destination is the state the page is ALREADY in, so they have nowhere to go.
     *
     * `EMPTY-STATES-85`: on its own tab a count chip is a real `<button>` whose handler resolves to
     * the current route state, `useHistoryBackedState` skips the write because the URL is unchanged,
     * and not even a history entry is pushed. Nothing happens — and since `EMPTY-STATES-59` gave the
     * chevron a resting state on a coarse pointer, it is the most navigational-looking thing on a
     * screen whose whole purpose is to give a member with nothing a next step. A member who taps it
     * reasonably concludes the page is stuck.
     *
     * Listed here rather than decided here, for the same reason `onCountNavigate` takes a CHIP name
     * and not a section: the public profile and the dashboard route to different section
     * vocabularies, so only the page can know which of its chips is a no-op. Each host derives this
     * from the same mapping its `handleCountNavigate` uses, so the two cannot disagree.
     *
     * The chip keeps its label and its count — `buildStatBadges` records that both relationship
     * chips must state their zero even when they cannot navigate (`EMPTY-STATES-28`). This changes
     * the AFFORDANCE, never the presence: `CardMetadataBadges` renders the inert caption branch for
     * an item with no `onClick`, so the button, the chevron and the 44px slug all go together.
     */
    inertCountTargets?: readonly UserHeaderCountTarget[];

    /**
     * `EMPTY-STATES-71` — the counts are still being fetched, so hold their row's height open.
     *
     * The five counts have no server seed on the dashboard: they are `null` until the bootstrap
     * response lands. `buildStatBadges` drops a badge whose label is `null` and `CardMetadataBadges`
     * returns `null` when nothing survives, so until then the strip is not merely blank, it is ABSENT
     * FROM THE LAYOUT — and when it arrives it is inserted ABOVE the content, moving everything below
     * it after that content has already been painted and after a thumb may already be in flight.
     *
     * A host that knows the fetch is outstanding passes `true` and the row keeps one chip-row of
     * height. It must go back to `false` when the fetch SETTLES — success or failure — or a failed
     * request leaves a permanent empty band, which is the dead space `EMPTY-STATES-98` files
     * elsewhere. A host with server-seeded counts (the public profile) never passes it at all.
     *
     * Seeding the counts server-side is the better fix and is the row's first direction; this is its
     * second, and it is the half a frontend batch can land. It removes the whole shift on desktop,
     * where the settled strip is one row, and about half of it at phone width, where it wraps to two.
     */
    countsPending?: boolean;

    /**
     * `EMPTY-STATES-82` — the addressable URL each count chip resolves to.
     *
     * Every count chip navigates, and until now not one of them was an anchor: a screen reader
     * announced "37 posts, button" with nothing saying it leaves the view, and nobody could
     * middle-click or Cmd-click one to open a second list in a new tab — on a surface whose entire job
     * is moving between seven lists of your own content.
     *
     * A separate callback from `onCountNavigate`, and a CHIP name rather than a URL map, for the same
     * reason that one takes a chip name: the public profile and the dashboard route to different
     * section vocabularies, so only the page can turn a chip into an address. A host that returns
     * `undefined`, or does not pass this at all, keeps the `<button>` it had — the anchor is strictly
     * an upgrade, never a requirement.
     *
     * The href and the pushed history entry must be built by ONE function in the host. Two copies of
     * the mapping is how the address bar and the anchor come to disagree.
     */
    countHrefFor?: (target: UserHeaderCountTarget) => string | undefined;
};

/**
 * The six profile counts, built once — the single list of which chips exist and in what order,
 * rendered by the identity card on every surface.
 */
type UserHeaderCounts = Pick<
    UserHeaderProps,
    | "discussionsCount"
    | "argumentsCount"
    | "agreementsReceivedCount"
    | "mindsChangedReceivedCount"
    | "followersCount"
    | "followingCount"
>;

type UserHeaderStatBadge = {
    key: UserHeaderCountTarget;
    label: string | null;
    icon: React.ElementType;
};

function buildStatBadges(localizer: Localizer, counts: UserHeaderCounts): UserHeaderStatBadge[] {
    const badges: UserHeaderStatBadge[] = [
        { key: "discussions", label: getFriendlyCountBadgeLabel(localizer, "badge.user.discussions", counts.discussionsCount), icon: MessageSquareText },
        { key: "arguments", label: getFriendlyCountBadgeLabel(localizer, "badge.user.thoughts", counts.argumentsCount), icon: MessageSquareQuote },
        { key: "agreements", label: getFriendlyCountBadgeLabel(localizer, "badge.user.upvotes", counts.agreementsReceivedCount), icon: ArrowBigUp },
        // Sparkles is the mind-changed marker everywhere else in the product (the thought badge and the
        // post-header badge both use it), so the profile chip inherits it rather than minting a second
        // visual language for one feature.
        { key: "minds-changed", label: getFriendlyCountBadgeLabel(localizer, "badge.user.minds_changed", counts.mindsChangedReceivedCount), icon: Sparkles },
        // Both relationship chips state a zero. They are also the only visible route to the
        // relationships tabs, so at zero the old behavior removed the answer AND the navigation: the
        // page never said "0 followers" anywhere, and a closed two-option sub-section dropdown shows
        // only the option already selected, so nothing on the page said the sibling tab existed
        // (ux-critique `EMPTY-STATES-28` (a) and (b)). A count that never arrived still drops its chip,
        // which is the distinction the row is actually about.
        //
        // Both labels are ICU plurals whose zero branch is written for this, so the chips read
        // "0 followers" and "Not following anyone yet" rather than "Following 0".
        { key: "followers", label: getFriendlyCountBadgeLabel(localizer, "badge.user.followers", counts.followersCount, { showZero: true }), icon: Users },
        { key: "following", label: getFriendlyCountBadgeLabel(localizer, "badge.user.following", counts.followingCount, { showZero: true }), icon: UserPlus2 },
    ];

    return badges.filter((item) => Boolean(item.label));
}

/**
 * The badge items in the shape CardMetadataBadges wants.
 *
 * The chip's accessible name is the count label it already renders ("4 thoughts"), which is exactly the
 * right name for a control that opens the thoughts — so no chip mints a localization key of its own.
 */
function toBadgeItems(
    badges: ReturnType<typeof buildStatBadges>,
    onCountNavigate?: (target: UserHeaderCountTarget) => void,
    inertCountTargets: readonly UserHeaderCountTarget[] = [],
    countHrefFor?: (target: UserHeaderCountTarget) => string | undefined,
) {
    return badges.map((item) => {
        const Icon = item.icon;
        // `EMPTY-STATES-85` — withholding the handler is the whole mechanism. `CardMetadataBadges`
        // forks on `item.onClick`, so an inert target falls to the caption `<span>`: no button, no
        // chevron, no `touch-hit-44` slug, and the label and count untouched.
        const inert = inertCountTargets.includes(item.key);
        const navigates = Boolean(onCountNavigate) && !inert;
        return {
            key: item.key,
            icon: <Icon className="size-3.5" aria-hidden={true} />,
            label: item.label,
            onClick: navigates && onCountNavigate ? () => onCountNavigate(item.key) : undefined,
            // `EMPTY-STATES-82`. Only where the chip actually navigates: an inert chip is a caption
            // (`EMPTY-STATES-85`), and a caption with an href would put back the affordance that row
            // deliberately removed. `CardMetadataBadges` upgrades to an `<a>` only when it has both.
            href: navigates ? countHrefFor?.(item.key) : undefined,
            dataPlaywrightId: `user-header-count-${item.key}`,
        };
    });
}

/**
 * One resting height for every pill in the control row (`DASHBOARD-34`).
 *
 * The row mixes two size scales that do not agree. `SimpleSelect size="sm"` resolves
 * `data-[size=sm]:h-9` (36px) and `CtaButton size="sm"` resolves `h-8` (32px), so the section and
 * sub-section triggers painted 36px while Metrics and Edit painted 32px and the row read as ragged
 * rather than as a deliberate group. Measured on the frames, confirmed in `SimpleSelect.tsx` and
 * `ctaShared.ts`.
 *
 * Declared ONCE, on the row, rather than as a class at one page's call site — which is what the finding
 * asked for ("a shared control-height decision, not a one-off class"). Everything the row is given
 * inherits it: the selects, `extraControls` (the language-scope select and the deleted-content pill),
 * and whatever a later page puts in `actionSlot`. A per-call-site override would leave the next control
 * added to this row back at 32px.
 *
 * A descendant selector rather than a prop, because the row does not own the components it is handed —
 * `extraControls` and `actionSlot` are arbitrary nodes from the page. `link`-variant CTAs are unaffected
 * (they carry `!h-auto`, which wins), which is right: a link is inline text, not a pill.
 */
const CONTROL_ROW_PILL_HEIGHT_CLASS = "[&_button]:h-9";

/**
 * The pointer the `DASHBOARD-44` fold applies to.
 *
 * Pointer, not width: the finding is a phone finding, and `03-tap-targets.css` already treats
 * `(pointer: coarse)` as this repo's statement of "a thumb is doing this". A width breakpoint would
 * also fold a narrow desktop window, where the row has never wrapped badly and the density is wanted.
 */
const COARSE_POINTER_MEDIA_QUERY = "(pointer: coarse)";

/** The section list the header falls back to when a caller passes none. Shared by both renderers. */
function defaultSectionOptions(localizer: Localizer): HeaderTabOption[] {
    return [
        { key: "discussions", value: "discussions", label: localizer("dashboard.tab.discussions"), icon: MessageSquareText },
        { key: "followers", value: "followers", label: localizer("discussion.section.followers"), icon: Users },
        { key: "following", value: "following", label: localizer("discussion.section.followings"), icon: UserPlus2 },
        { key: "groups", value: "groups", label: localizer("dashboard.tab.groups"), icon: Users },
        { key: "series", value: "series", label: localizer("dashboard.tab.series"), icon: List },
    ];
}

/**
 * The section / sub-section / ordering / language row.
 *
 * In `"wide"` mode {@link UserHeader} renders this itself, below the card and right-aligned — the
 * shape the page shipped with. In `"rail"` mode the page renders it at the top of the main column
 * instead, left-aligned, so the profile opens as a control row and then immediately content.
 */
export function UserHeaderControls(props: UserHeaderProps) {
    const {
        localizer,
        section,
        onSectionChange,
        sectionOptions,
        secondarySection,
        onSecondarySectionChange,
        secondaryOptions,
        orderingValue,
        onOrderingChange,
        orderingOptions,
        orderingLabel,
        extraControls,
        listCountSlot,
        collapsibleControls,
        collapsibleFilters = false,
        filtersActive = false,
        layout = "wide",
    } = props;
    const sectionSelectOptions = sectionOptions ?? defaultSectionOptions(localizer);

    /**
     * `DASHBOARD-44` — the fold, and the one piece of state behind it.
     *
     * `null` means "the member has not expressed a preference", so the panel follows whether a filter is
     * applied; once they use the trigger their choice wins. ONE state, one derived boolean — not a
     * `useState` mirroring `filtersActive` through an effect, which is the two-copies-synced-both-ways
     * shape rule 31 exists to forbid.
     */
    const [filtersOpenOverride, setFiltersOpenOverride] = React.useState<boolean | null>(null);
    const coarsePointer = useMediaQueryMatches(COARSE_POINTER_MEDIA_QUERY);
    const filtersOpen = filtersOpenOverride ?? filtersActive;
    const filtersPanelId = React.useId();

    const orderingSelect = orderingOptions && orderingValue !== undefined && onOrderingChange ? (
        <SimpleSelect
            value={orderingValue}
            onValueChange={onOrderingChange}
            options={orderingOptions}
            placeholder={orderingLabel}
            ariaLabel={orderingLabel}
            size="sm"
            dataPlaywrightId="user-header-ordering-select"
        />
    ) : null;
    // `DASHBOARD-86` (round 38 A10): fold only when there are TWO or more foldable controls. A fold
    // holding one select costs a row for its trigger and a tap to reach the select, and thins nothing
    // (the drafts facet, a section whose only foldable control is the ordering). `collapsibleControls`
    // counts per top-level child, so a Fragment counts as one; a host folding several passes an array.
    const foldableControlCount = (orderingSelect ? 1 : 0) + React.Children.toArray(collapsibleControls).length;
    const foldFilters = collapsibleFilters && coarsePointer && foldableControlCount >= 2;
    // Rendered in exactly ONE of the two places below, never both — see `collapsibleFilters`.
    const foldedFilters = foldFilters ? null : (
        <>
            {orderingSelect}
            {collapsibleControls}
        </>
    );
    const hasFoldableFilters = Boolean(orderingSelect || collapsibleControls);

    const hasHeaderControls = Boolean(
        sectionSelectOptions.length > 0 ||
        (secondaryOptions?.length ?? 0) > 0 ||
        (orderingOptions?.length ?? 0) > 0 ||
        extraControls ||
        listCountSlot ||
        collapsibleControls
    );

    if (!hasHeaderControls) {
        return null;
    }

    const rail = layout === "rail";

    return (
        <div className={rail ? "mb-3 flex w-full flex-col items-start gap-2" : "mb-4 flex w-full justify-end sm:mb-5"}>
            <div
                className={`flex w-auto flex-wrap items-center gap-2 ${CONTROL_ROW_PILL_HEIGHT_CLASS} ${rail ? "justify-start" : "justify-end"}`}
                data-playwright-id="user-header-control-row"
            >
                {/* Both names state the control's JOB, never its value — `SimpleSelect` already
                    announces the value, so echoing it leaves a combobox that renames itself on every
                    change and reads as landing somewhere new rather than as having filtered.

                    All three selects in this row now pass one. DASHBOARD-6 was that these two did not
                    while the ordering select always had; it is closed, and both `ariaLabel` props below
                    are what closed it. A name was only half of that finding: it does nothing for the
                    sighted member who cannot tell the pill is a control, which is why the
                    deleted-content entry point is a visible pill in `extraControls` rather than a
                    better label here. */}
                {sectionSelectOptions.length > 0 ? (
                    <SimpleSelect
                        value={section}
                        onValueChange={onSectionChange}
                        options={sectionSelectOptions}
                        ariaLabel={localizer("profile.section.label")}
                        size="sm"
                        dataPlaywrightId="user-header-section-select"
                    />
                ) : null}

                {secondaryOptions && secondarySection !== undefined && onSecondarySectionChange ? (
                    <SimpleSelect
                        value={secondarySection}
                        onValueChange={onSecondarySectionChange}
                        options={secondaryOptions}
                        ariaLabel={localizer("profile.subsection.label")}
                        size="sm"
                        dataPlaywrightId="user-header-subsection-select"
                    />
                ) : null}

                {/* `DASHBOARD-24` / wave-12 Q8 — the size of the list this row is controlling, beside
                    the control that names it. Bare, no wrapper: the slot is `null` for a collection
                    the viewer may not see, and a wrapper would hold a gap open in that case. */}
                {listCountSlot}

                {foldedFilters}

                {/* `DASHBOARD-44` — the fold's trigger, on a coarse pointer only.

                    A `rounded-full` outline pill, so it is the same object as every other control in
                    this row and inherits the row's own `[&_button]:h-9` (`DASHBOARD-34`); no
                    `touch-hit-44` is added, because that expander is wrong for a control sharing a
                    line with siblings and the row already carries the coarse floor its pills need.

                    `aria-expanded` + `aria-controls` rather than a bare toggle: the panel is a real
                    disclosure and a screen reader has to be able to tell that the two selects still
                    exist. Existing key — `search.filter.button` is already the bare noun "Filters" in
                    all 25 catalogs, so this costs no copy (rule 11: reuse before minting). */}
                {foldFilters && hasFoldableFilters ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full"
                        aria-expanded={filtersOpen}
                        aria-controls={filtersPanelId}
                        onClick={() => setFiltersOpenOverride(!filtersOpen)}
                        data-playwright-id="user-header-filters-toggle"
                        data-filters-open={filtersOpen ? "true" : "false"}
                    >
                        <SlidersHorizontal className="size-4" aria-hidden="true" />
                        {localizer("search.filter.button")}
                    </Button>
                ) : null}

                {extraControls}
            </div>

            {/* `DASHBOARD-44` — the folded filters, on their own line, only while open.

                Not rendered-and-hidden: an unmounted select is one fewer control in the resting row,
                which is the whole finding, and it also keeps exactly one
                `user-header-ordering-select` in the document at any time (see `collapsibleFilters`). */}
            {foldFilters && hasFoldableFilters ? (
                <div
                    id={filtersPanelId}
                    className={`flex w-full flex-wrap items-center gap-2 ${CONTROL_ROW_PILL_HEIGHT_CLASS}`}
                    data-playwright-id="user-header-filters-panel"
                    hidden={!filtersOpen}
                >
                    {filtersOpen ? (
                        <>
                            {orderingSelect}
                            {collapsibleControls}
                        </>
                    ) : null}
                </div>
            ) : null}

        </div>
    );
}

export function UserHeader({
                               username,
                               localizer,
                               initialProfileImageUrl,
                               initialDescription,
                               initialCreationDate,
                               section,
                               onSectionChange,
                               sectionOptions,
                               secondarySection,
                               onSecondarySectionChange,
                               secondaryOptions,
                               orderingValue,
                               onOrderingChange,
                               orderingOptions,
                               orderingLabel,
                               extraControls,
                               listCountSlot,
                               collapsibleControls,
                               collapsibleFilters = false,
                               filtersActive = false,
                               achievementsSlot,
                               statementSlot,
                               showcaseSlot,
                               newsletterSlot,
                               visitorInsightSlot,
                               actionSlot,
                               countsPending = false,
                               countHrefFor,
                               showDefaultActions = true,
                               onAvatarClick,
                               layout = "wide",
                                initiallyFollowing = false,
                                canMessage,
                                welcomeHost = false,
                                sensitive = false,
                                discussionsCount,
                                argumentsCount,
                                agreementsReceivedCount,
                                mindsChangedReceivedCount,
                                followersCount,
                                followingCount,
                                onCountNavigate,
                                inertCountTargets,
                           }: UserHeaderProps) {
    const rail = layout === "rail";
    const [profileImageUrl, setProfileImageUrl] = React.useState(initialProfileImageUrl ?? null);
    const [profileDescription, setProfileDescription] = React.useState(initialDescription ?? null);
    const [profileSensitive, setProfileSensitive] = React.useState(Boolean(sensitive));
    const dateFormatter = new DateFormatter(localizer);
    const createdFormatted = initialCreationDate ? dateFormatter.timeSince(initialCreationDate) : null;
    const memberSince = createdFormatted ? localizer("user.member-since", createdFormatted) : null;
    const description = profileDescription?.trim() ?? "";
    // `EMPTY-STATES-98` / D16 — whether the banner has anything to show. See the ProfileBanner call
    // below for why "cover" is the member's avatar here and why that is not a naming slip.
    const hasCoverImage = Boolean(profileImageUrl?.trim());
    const normalizedUsername = normalizeDisplayUsername(username);
    const displayUsername = getDisplayUsername(username, localizer);
    // D31: on the support account's own profile the member is still meeting it as a correspondent, so it
    // is presented by its alias — and WITHOUT the @handle prefix, because "@Seavien Support" reads as a
    // username that could be typed and cannot be. Every other account keeps the @handle.
    // The raw username still drives the avatar letter, the follow buttons and every link on this page;
    // only this label changes. The official badge that sits beside it is slice 3F.
    const supportUsername = useSupportUsername();
    const viewingSupportAccount = isSupportParticipant(normalizedUsername, supportUsername);
    const primaryUsernameLabel = viewingSupportAccount
        ? localizer("support.display_name")
        : normalizedUsername ? `@${normalizedUsername}` : displayUsername;

    const statBadges = buildStatBadges(localizer, {
        discussionsCount,
        argumentsCount,
        agreementsReceivedCount,
        mindsChangedReceivedCount,
        followersCount,
        followingCount,
    });

    React.useEffect(() => {
        setProfileImageUrl(initialProfileImageUrl ?? null);
    }, [initialProfileImageUrl]);

    React.useEffect(() => {
        setProfileDescription(initialDescription ?? null);
    }, [initialDescription]);

    React.useEffect(() => {
        setProfileSensitive(Boolean(sensitive));
    }, [sensitive]);

    React.useEffect(() => {
        if (!normalizedUsername) {
            return;
        }

        return onProfileUpdated((payload) => {
            if (!payload || payload.username !== normalizedUsername) {
                return;
            }

            if (payload.profileImageUrl !== undefined) {
                setProfileImageUrl(payload.profileImageUrl ?? null);
            }
            if (payload.profileDescription !== undefined) {
                setProfileDescription(payload.profileDescription ?? null);
            }
            if (payload.sensitive !== undefined) {
                setProfileSensitive(Boolean(payload.sensitive));
            }
        });
    }, [normalizedUsername]);

    const renderedActions = actionSlot ?? (showDefaultActions && normalizedUsername
        // `align` is the rail's left-aligned intent made explicit, instead of the wrapper below trying
        // to express it and losing to the child's own `ml-auto` (ux-refine-user decision ①). Passed in
        // BOTH directions rather than only in the rail branch, so the wide layout's alignment is a
        // stated choice here rather than an inherited default that a change to `UserButtons` could move.
        ? <UserButtons username={normalizedUsername} initiallyFollowing={initiallyFollowing} canMessage={canMessage} surface="user_profile_header" localizer={localizer} align={rail ? "start" : "end"} />
        : null);

    return (
        <>
            <section className={rail ? "w-full" : "mb-4 w-full sm:mb-5"}>
                {/* No `shadow-[var(--surface-shadow-soft)]` on this card. That token is `none` in
                    `:root` and `0 10px 24px rgb(0 0 0 / 0.24)` in `.dark`, so declaring it was dead
                    weight in light and a flat-X rule-1 violation in dark — on the largest resting
                    surface of the profile page, in the theme no refine run has ever captured. The
                    hairline border is the elevation.

                    `rounded-xl` is the ONE card radius (design language rule 2), and the same string the
                    two group headers and the series header use. This card reached `rounded-2xl` by a
                    half-step out of an arbitrary `rounded-[2rem]`; the conformance was decided
                    2026-08-09. The guard asserts the four header cards AGREE rather than pinning the
                    literal, so re-tuning the card radius has to move all four. */}
                {/* The browser oracle hooks HERE, on the card box, not on the `<section>` above it.
                    The section is a layout wrapper that never carried an elevation treatment in any
                    theme, so a computed-shadow assertion aimed at it passes for free — which is what
                    `profile-rail-layout-invariants.spec.mjs`'s pre-existing selector would have done. */}
                <div className="overflow-hidden rounded-xl border border-border bg-card" data-playwright-id="user-identity-card">
                    {/* The rail's banner is short on purpose. At 22rem the card is the tallest thing in
                        the column, and every pixel of decorative banner is a pixel the trophy case and
                        the actions get pushed down by — §2.5's "a rail long enough to scroll past the
                        feed's first screen is a header again". */}
                    {/* Shorter still below `lg`, where the rail is not beside the content but stacked
                        above it — decorative height there is charged directly to the first screenful,
                        which is the same argument one breakpoint further down. */}
                    {/* `EMPTY-STATES-98` / decision D16, 2026-08-26 — and it collapses further when
                        there is nothing in it.

                        Code-verified twice now: THERE IS NO PROFILE COVER-IMAGE FEATURE. Only
                        discussions have cover images; the `imageUrl` this band is handed is the
                        member's own avatar, used as a background wash. So on a card with no avatar the
                        band is a flat gradient reserving height for content that cannot ever arrive —
                        on the emptiest self-view it was 112 of a 217px desktop rail card, 52% of it —
                        and "let the member fill it" is a feature request, not a fix.

                        ⚠️ Two things a later reader must know before touching this. It changes the card
                        for VISITORS too: the same component draws every profile, and the branch is on
                        the picture, not on who is looking. And it REVERSES the 2026-08-08 "the rail's
                        banner is short on purpose" decision in the one direction that decision did not
                        consider — that note was about height, this is about reserving height for
                        something that does not exist.

                        It collapses to a FLOOR, never to zero. The avatar below is `-mt-12` in rail and
                        `-mt-14 sm:-mt-18` in wide, inside a card with `overflow-hidden`, so a band
                        shorter than that overlap clips the avatar against the card's top border. rail
                        keeps `min-h-16` (64px against a 48px overlap) and simply drops the `lg` step;
                        wide comes down to `min-h-24 sm:min-h-28`. The PHONE band is therefore unchanged
                        and that is deliberate: it is already at the floor, and going below it means
                        moving the avatar, which is a composition change no row asked for. */}
                    {/* SV-78 ⑦ (round-29 D15) made that composition change, below `lg` only: the rail's
                        avatar now sits BESIDE the name row there instead of overlapping the band, so the
                        overlap floor no longer binds on a phone and the EMPTY band drops to `min-h-8`.
                        At `lg` it keeps the `min-h-16` floor above, and the band that carries a picture is
                        unchanged at every width. */}
                    <ProfileBanner
                        imageUrl={profileImageUrl}
                        className={hasCoverImage
                            ? (rail ? "min-h-16 lg:min-h-28" : "min-h-40 sm:min-h-52")
                            : (rail ? "min-h-8 lg:min-h-16" : "min-h-24 sm:min-h-28")}
                        dataPlaywrightId={hasCoverImage ? "user-identity-card-banner" : "user-identity-card-banner-collapsed"}
                    />

                    <div className={rail ? "relative px-4 pb-4 pt-3 sm:px-5 sm:pb-5 lg:pt-0" : "relative px-4 pb-4 sm:px-6 sm:pb-6"}>
                        <div className="flex flex-col gap-4">
                            {/* Both variants keep this nesting — profile row / actions row / achievements
                                slot as the three children of one stack (SV-78 ②, round-29 D15: actions
                                BEFORE the trophy case). UserHeader.spec.tsx walks `.user-overview` up two
                                levels to assert that order, and profile-achievements-cabinet.spec.mjs
                                asserts it by geometry plus the rail sitting INSIDE this card's box. The
                                rail variant changes measures, never structure. Below `lg` the rail's
                                profile row is a ROW (avatar beside the name, ⑦); `lg:` restores the
                                stacked desktop card. */}
                            <div className={rail ? "flex min-w-0 flex-row items-start gap-3 lg:flex-col lg:items-stretch" : "flex min-w-0 flex-col gap-4 sm:flex-row sm:items-end"}>
                                <ProfileImage localizer={localizer}
                                    imageUrl={profileImageUrl ?? undefined}
                                    username={normalizedUsername ?? undefined}
                                    mode="user"
                                    // `border-card`, not `border-[var(--card)]` — same colour, semantic
                                    // name. Both group headers already write this ring that way.
                                    className={rail
                                        ? "size-14 shrink-0 border-4 border-card lg:-mt-12 lg:size-20"
                                        : "-mt-14 size-24 border-4 border-card sm:-mt-18 sm:size-32 lg:size-36"}
                                    hoverable={true}
                                    role="terminal"
                                    onClick={onAvatarClick ? () => onAvatarClick(profileImageUrl) : undefined}
                                />

                                <div className={rail ? "user-overview min-w-0 flex-1" : "user-overview min-w-0 flex-1 pt-1 sm:pb-1"}>
                                    <div className={rail ? "flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-3"}>
                                        <div className="inline-flex max-w-full flex-wrap items-center gap-2">
                                            {/* A member's name is a proper noun; without this the
                                                reader's browser translator rewrites it, which is how
                                                the member `Dei` reached Spanish readers as `Dios`
                                                (2026-09-03). Conditional, because this same heading
                                                renders our localized support display name and the
                                                localized deleted-user label, both of which must stay
                                                translatable. Design: designs/01-now/2026-09-03-member-
                                                display-names-excluded-from-browser-translation-design.md */}
                                            <h1 translate={normalizedUsername && !viewingSupportAccount ? "no" : undefined}
                                                className={rail
                                                ? "username max-w-full truncate text-xl font-semibold tracking-tight text-foreground"
                                                : "username max-w-full truncate text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"}>
                                                {primaryUsernameLabel}
                                            </h1>
                                            {viewingSupportAccount ? (
                                                <Badge
                                                    variant="default"
                                                    className="py-0"
                                                    data-playwright-id="user-official-badge"
                                                >
                                                    <BadgeCheck aria-hidden="true" />
                                                    {localizer("badge.account.official")}
                                                </Badge>
                                            ) : null}
                                            {/* Welcome hosts D3/A17/A21: same Badge styling as Official, only on the
                                                server-computed flag. No count (D9, T5). */}
                                            {welcomeHost ? (
                                                <Badge
                                                    variant="default"
                                                    className="py-0"
                                                    data-playwright-id="user-welcome-host-badge"
                                                >
                                                    <HeartHandshake aria-hidden="true" />
                                                    {localizer("badge.welcome_host")}
                                                </Badge>
                                            ) : null}
                                            {profileSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                                        </div>

                                        {memberSince ? (
                                            <div className={rail
                                                ? "inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-1 text-xs text-neutral-text"
                                                : "inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5 text-sm text-neutral-text"}>
                                                <CalendarDays className="size-3.5" />
                                                <span>{memberSince}</span>
                                            </div>
                                        ) : null}
                                    </div>

                                    {/* The `description` class this used to carry was NOT a hook and NOT
                                        inert: `04-forms.css` declared it outside any `@layer`, so it beat
                                        these utilities on colour AND on the whole `margin` shorthand. Both
                                        the rule and the class are retired; the stable hook is the
                                        `data-playwright-id` below, which `user.jte` mirrors and
                                        `UserControllerTests` anchors its ordering assertion on. Guarded by
                                        `src/main/css/legacyGlobalClassOverrides.contract.test.ts`. */}
                                    {description ? (
                                        <p data-playwright-id="profile-description"
                                            className={rail
                                                ? "mt-2 text-sm leading-6 text-muted-foreground"
                                                : "mt-3 max-w-3xl text-sm leading-6 text-muted-foreground sm:text-[15px]"}>
                                            {description}
                                        </p>
                                    ) : null}

                                    {/* Slice F3's owner statement sits between the description and the
                                        counts: it is self-authored identity, so it belongs with the
                                        description rather than with the metrics. Bare, no wrapper —
                                        see `statementSlot`. */}
                                    {statementSlot}

                                    {/* `EMPTY-STATES-71` — the wrapper also renders while
                                        `countsPending`, holding one chip-row of height open so the
                                        strip is INSERTED into reserved space rather than into the
                                        document (the dashboard's counts have no server seed). 26px is
                                        the chip's own resting height and is arithmetic, not a
                                        measurement: `text-xs` is a 16px line box, `py-1` adds 4 + 4,
                                        the border adds 1 + 1. A floor, not a fixed height — the
                                        settled strip is free to be taller. Released when the fetch
                                        SETTLES, not when it succeeds; see the prop. */}
                                    {statBadges.length > 0 || countsPending ? (
                                        <div
                                            className={statBadges.length === 0 ? "mt-3 min-h-[26px]" : "mt-3"}
                                            data-playwright-id="user-identity-card-counts"
                                            data-counts-pending={statBadges.length === 0 && countsPending ? "true" : undefined}
                                        >
                                            <CardMetadataBadges
                                                className="gap-1.5"
                                                badgeClassName="border-border/60 bg-muted px-2.5 py-1 text-xs"
                                                items={toBadgeItems(statBadges, onCountNavigate, inertCountTargets, countHrefFor)}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            </div>

                            {renderedActions ? (
                                <div className={rail
                                    ? "relative z-layer-raised-front flex w-full flex-wrap items-center gap-2"
                                    : "relative z-layer-raised-front flex w-full flex-wrap items-center gap-2 lg:justify-end"}>
                                    {renderedActions}
                                </div>
                            ) : null}

                            {/* Rendered bare, with no wrapper: the rail returns `null` until the
                                profile has its first award, and an element that renders null produces
                                no DOM node — so the card's `gap-4` closes up on its own. Wrapping it
                                in a `<div>` here would leave an empty box holding a gap open on every
                                profile that has earned nothing yet, which is decision §15.2's banned
                                empty trophy case wearing a different hat. LAST since SV-78 ② (round-29
                                D15): Follow is what a visitor came to press, so it sits above the trophies. */}
                            {achievementsSlot}
                        </div>
                    </div>
                </div>
            </section>

            {/* Slices F1 and F2 stack BELOW the identity card, in the same column. Guarded as a pair
                rather than rendered bare, because unlike the in-card slots these two need a gap between
                them — and a wrapper that always rendered would hold `mt-4` open on every profile that
                has neither, which is the empty-box shape the slot contract exists to avoid. The guard
                makes the wrapper appear exactly when there is something to space.

                `empty:hidden` closes the half of that the ternary structurally cannot (added with F1,
                2026-08-11). The guard tests whether a slot was PASSED, and both slots are filled with
                components that fetch and then decide to render nothing — so a React element is always
                truthy here while the DOM below is often empty. Without `empty:hidden` that is 16px of
                `mt-4` under the identity card of every profile with no showcase and no insight, which
                is the empty-box shape the comment above exists to prevent, arriving one indirection
                later. `:empty` matches an element with no child nodes at all, which is exactly the
                state a slot rendering `null` produces. */}
            {showcaseSlot || newsletterSlot || visitorInsightSlot ? (
                <div className="mt-4 flex w-full flex-col gap-4 empty:hidden empty:mt-0" data-playwright-id="user-profile-blocks">
                    {showcaseSlot}
                    {newsletterSlot}
                    {visitorInsightSlot}
                </div>
            ) : null}

            {/* In "rail" mode the page owns the control row and puts it at the top of the main column,
                so the card stops here. Rendering it in both places would put two
                `user-header-section-select` hooks in one document. */}
            {rail ? null : (
                <UserHeaderControls
                    localizer={localizer}
                    section={section}
                    onSectionChange={onSectionChange}
                    sectionOptions={sectionOptions}
                    secondarySection={secondarySection}
                    onSecondarySectionChange={onSecondarySectionChange}
                    secondaryOptions={secondaryOptions}
                    orderingValue={orderingValue}
                    onOrderingChange={onOrderingChange}
                    orderingOptions={orderingOptions}
                    orderingLabel={orderingLabel}
                    extraControls={extraControls}
                    listCountSlot={listCountSlot}
                    collapsibleControls={collapsibleControls}
                    collapsibleFilters={collapsibleFilters}
                    filtersActive={filtersActive}
                    layout="wide"
                />
            )}
        </>
    );
}
